import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import {
  createSkuAttribute,
  deleteSkuAttribute,
  getSkuAttributeCategories,
  getSkuAttributes,
  updateSkuAttribute,
} from '@/api/skuAttributes'
import { toast } from '@/stores/useToastStore'
import type { AttributeFormData } from '@/types/attribute'

interface ApiErrorPayload {
  detail?: string
  title?: string
}

function getApiErrorMessage(
  error: AxiosError<ApiErrorPayload>,
  fallback: string
) {
  return error.response?.data?.detail ?? error.response?.data?.title ?? fallback
}

export function useSkuAttributes(skip = 0, limit = 100) {
  return useQuery({
    queryKey: ['sku-attributes', skip, limit],
    queryFn: () => getSkuAttributes(skip, limit),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

export function useSkuAttributeCategories(
  parentId?: string | null,
  enabled = true
) {
  return useQuery({
    queryKey: [
      'sku-attribute-categories',
      parentId === undefined ? 'all' : (parentId ?? 'null'),
    ],
    queryFn: () => getSkuAttributeCategories(parentId),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

export function useCreateSkuAttribute() {
  const queryClient = useQueryClient()

  return useMutation<unknown, AxiosError<ApiErrorPayload>, AttributeFormData>({
    mutationFn: createSkuAttribute,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sku-attributes'] })
      await queryClient.invalidateQueries({
        queryKey: ['sku-attribute-categories'],
      })
    },
    onError: error => {
      toast.error(getApiErrorMessage(error, 'Failed to create attribute'))
    },
  })
}

export function useUpdateSkuAttribute() {
  const queryClient = useQueryClient()

  return useMutation<
    unknown,
    AxiosError<ApiErrorPayload>,
    { id: string; data: AttributeFormData }
  >({
    mutationFn: ({ id, data }) => updateSkuAttribute(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sku-attributes'] })
      await queryClient.invalidateQueries({
        queryKey: ['sku-attribute-categories'],
      })
    },
    onError: error => {
      toast.error(getApiErrorMessage(error, 'Failed to update attribute'))
    },
  })
}

export function useDeleteSkuAttribute() {
  const queryClient = useQueryClient()

  return useMutation<unknown, AxiosError<ApiErrorPayload>, string>({
    mutationFn: id => deleteSkuAttribute(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sku-attributes'] })
      await queryClient.invalidateQueries({
        queryKey: ['sku-attribute-categories'],
      })
    },
    onError: error => {
      toast.error(getApiErrorMessage(error, 'Failed to delete attribute'))
    },
  })
}
